import React, { useEffect, useState } from 'react';
import './footer.styles.scss';
import { ClockIcon, ExternalLinkIcon, FacebookIcon, GoogleIcon } from '@/components';
import useBuildDate from '@/hooks/use-build-date';
import { HOURS, SpecialHours, getUpcomingSpecialHours } from '@/config/hours.config';
import {
  ADDRESS,
  BUSINESS_NAME,
  DIRECTIONS_URL,
  PHONE,
  WRITE_REVIEW_URL,
  getPhoneLinkProps,
} from '@/config/contact.config';
import { trackEvent } from '@/utils/analytics';

const PHONE_LINK_PROPS = getPhoneLinkProps('footer');

const SOCIAL_LINKS = [
  {
    label: 'Zeera By The Bay on Facebook',
    href: 'https://www.facebook.com/zeerabtb/',
    Icon: FacebookIcon,
    onClick: () => trackEvent('social_click', { platform: 'facebook', placement: 'footer' }),
  },
  {
    label: 'Review Zeera By The Bay on Google',
    href: WRITE_REVIEW_URL,
    Icon: GoogleIcon,
    onClick: () => trackEvent('social_click', { platform: 'google_review', placement: 'footer' }),
  },
];

// "2026-10-12" -> "Mon, Oct 12"
const formatSpecialDate = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const lastUpdated = useBuildDate();
  // Set after mount so past holidays drop off without a rebuild
  const [holidayHours, setHolidayHours] = useState<SpecialHours[]>([]);

  useEffect(() => {
    setHolidayHours(getUpcomingSpecialHours());
  }, []);

  return (
    <footer className="footer">
      <div className="footer__content container">
        <div className="footer__brand">
          <img
            className="footer__logo"
            src="/zeera-logo-192x192.png"
            alt={BUSINESS_NAME}
            width="84"
            height="84"
          />
          <p className="footer__tagline">
            Indian restaurant serving Pickering, Ajax and Durham since 2009.
          </p>
          <div className="footer__socials">
            {SOCIAL_LINKS.map(({ label, href, Icon, onClick }) => (
              <a
                key={label}
                className="footer__social"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                onClick={onClick}
              >
                <Icon />
              </a>
            ))}
          </div>
        </div>
        <dl className="footer__info">
          <div className="footer__row">
            <dt className="footer__label">Address</dt>
            <dd className="footer__value">
              <address className="footer__address">
                <span>{ADDRESS.street}</span>
                <span>
                  {ADDRESS.city}, {ADDRESS.province} {ADDRESS.postalCode}
                </span>
              </address>
              <a
                className="footer__accent-link"
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('directions_click', { placement: 'footer' })}
                aria-label="Get directions (opens in a new tab)"
              >
                Get directions
                <ExternalLinkIcon className="footer__external-icon" aria-hidden="true" />
              </a>
            </dd>
          </div>
          <div className="footer__row">
            <dt className="footer__label">Phone</dt>
            <dd className="footer__value">
              <a className="footer__link" {...PHONE_LINK_PROPS}>
                {PHONE.display}
              </a>
            </dd>
          </div>
          <div id="hours" className="footer__row">
            <dt className="footer__label">Hours</dt>
            <dd className="footer__value">
              <ul className="footer__hours">
                {HOURS.map(({ dayOfWeek, timeRanges }) => (
                  <li key={dayOfWeek} className="footer__hours-row">
                    <span className="footer__day">{dayOfWeek.slice(0, 3)}</span>
                    <span className="footer__times">
                      {timeRanges.map((range) => (
                        <span key={range}>{range}</span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
              {holidayHours.length > 0 && (
                <div className="footer__holiday">
                  <p className="footer__holiday-heading">Holiday hours</p>
                  <ul className="footer__hours">
                    {holidayHours.map(({ date, label, timeRanges }) => (
                      <li key={date} className="footer__hours-row">
                        <span className="footer__day">
                          {label} ({formatSpecialDate(date)})
                        </span>
                        <span className="footer__times">
                          {timeRanges.length > 0
                            ? timeRanges.map((range) => <span key={range}>{range}</span>)
                            : 'Closed'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </dd>
          </div>
        </dl>
      </div>
      <div className="footer__bottom container">
        <span>
          © {currentYear} {BUSINESS_NAME}
        </span>
        <span className="footer__last-updated">
          <ClockIcon className="footer__clock-icon" aria-hidden="true" />
          Last updated: {lastUpdated}
        </span>
      </div>
    </footer>
  );
};

export default Footer;
